import { CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type {
  Project,
  ProjectChange,
  ProjectChangeReadinessCheck,
  ProjectSummary,
  ProjectThread,
  ProjectThreadResource,
} from '../../hooks/useProjects.js';

// ─── Attention items ──────────────────────────────────────────────────────────

export interface AttentionItem {
  id: string;
  kind: string;
  title: string;
  thread: ProjectThread | undefined;
  change: ProjectChange | undefined;
  tone: 'danger' | 'warning' | 'accent';
  href: string;
}

type RawBlocker = { changeId: number; reason?: string; title: string };

export function buildAttentionItems(params: {
  projectId: number;
  blockers: RawBlocker[];
  activeChanges: ProjectChange[];
  activeThreads: ProjectThread[];
  failedReadiness: ProjectChangeReadinessCheck[];
  warningReadiness: ProjectChangeReadinessCheck[];
  staleThreads: ProjectThread[];
  pendingDecisions?: ProjectThreadResource[];
}): AttentionItem[] {
  const {
    projectId,
    blockers,
    activeChanges,
    activeThreads,
    failedReadiness,
    warningReadiness,
    staleThreads,
    pendingDecisions = [],
  } = params;
  const workThreads = `/projects/${projectId}/work-threads`;
  return [
    ...blockers.map((blocker) => {
      const change = activeChanges.find((c) => c.id === blocker.changeId);
      const thread = change ? activeThreads.find((t) => t.id === change.projectThreadId) : undefined;
      return {
        id: `blocker-${blocker.changeId}`,
        kind: 'Blocker',
        title: blocker.reason || blocker.title,
        thread,
        change,
        tone: 'danger' as const,
        href: thread ? workThreadHref(projectId, thread.id, change?.id) : workThreads,
      };
    }),
    ...failedReadiness.slice(0, 3).map((check) => {
      const change = activeChanges.find((c) => c.id === check.changeId);
      const thread = change ? activeThreads.find((t) => t.id === change.projectThreadId) : undefined;
      return {
        id: `readiness-${check.changeId}-${check.check}`,
        kind: 'Readiness',
        title: `${check.label}: ${check.detail}`,
        thread,
        change,
        tone: 'danger' as const,
        href: thread ? workThreadHref(projectId, thread.id, change?.id) : workThreads,
      };
    }),
    ...pendingDecisions.slice(0, 2).map((decision) => {
      const thread = activeThreads.find((t) => t.id === decision.projectThreadId);
      const change = decision.projectChangeId
        ? activeChanges.find((c) => c.id === decision.projectChangeId)
        : undefined;
      const label = decision.role ?? `Decision #${decision.resourceId}`;
      return {
        id: `decision-${decision.id}`,
        kind: 'Approval',
        title: `${label} needs a decision.`,
        thread,
        change,
        tone: 'warning' as const,
        href: thread ? workThreadHref(projectId, thread.id, change?.id) : workThreads,
      };
    }),
    ...warningReadiness.slice(0, 2).map((check) => {
      const change = activeChanges.find((c) => c.id === check.changeId);
      const thread = change ? activeThreads.find((t) => t.id === change.projectThreadId) : undefined;
      return {
        id: `warning-${check.changeId}-${check.check}`,
        kind: 'Review',
        title: `${check.label}: ${check.detail}`,
        thread,
        change,
        tone: 'warning' as const,
        href: thread ? workThreadHref(projectId, thread.id, change?.id) : workThreads,
      };
    }),
    ...staleThreads.slice(0, 2).map((thread) => ({
      id: `stale-${thread.id}`,
      kind: 'Idle thread',
      title: `${thread.title} has had no movement for ${daysSince(thread.updatedAt)} days.`,
      thread,
      change: undefined as ProjectChange | undefined,
      tone: 'accent' as const,
      href: workThreadHref(projectId, thread.id),
    })),
  ].slice(0, 5);
}

export function formatWorkMode(mode: string): string {
  return mode.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function daysSince(iso: string): number {
  const time = new Date(iso).getTime();
  if (!Number.isFinite(time)) return 0;
  return Math.max(0, Math.floor((Date.now() - time) / 86_400_000));
}

export function formatShortRelative(iso: string): string {
  const days = daysSince(iso);
  if (days === 0) return 'today';
  if (days === 1) return '1d';
  if (days < 14) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

export function conversationHref(project: Project, message: string): string {
  const params = new URLSearchParams({
    projectId: String(project.id),
    subjectType: 'project',
    subjectId: String(project.id),
    mode: 'project-assistant',
    message,
  });
  return `/conversations/new?${params.toString()}`;
}

export function workThreadHref(projectId: number, threadId: number, changeId?: number): string {
  const params = new URLSearchParams({ thread: String(threadId) });
  if (changeId) params.set('change', String(changeId));
  return `/projects/${projectId}/work-threads?${params.toString()}`;
}

/**
 * Deep-link a thread/activity resource to ITS OWN detail page by id (verified
 * against src/modules/core/routes.tsx) — never to the entity's list. `decision`
 * has no detail route, so it falls back to its owning Work Thread context.
 */
export function resourceHref(
  resourceType: string | undefined,
  resourceId: string | undefined,
  projectId: number,
  threadId?: number | null,
): string {
  const threadFallback =
    threadId != null ? `/projects/${projectId}/work-threads?thread=${threadId}` : `/projects/${projectId}/work-threads`;
  if (!resourceId) return threadFallback;
  switch (resourceType) {
    case 'task':
      return `/tasks/${resourceId}`;
    case 'artifact':
      return `/artifacts/${resourceId}`;
    case 'workforce_member':
      return `/projects/${projectId}/workforce/${resourceId}`;
    case 'workflow_run':
      return `/agents/runs/${resourceId}`;
    case 'workstream':
      return `/projects/${projectId}/workstreams/${resourceId}/code`;
    case 'conversation':
      return `/conversations/${resourceId}`;
    default:
      return threadFallback;
  }
}

export function changeStatusStyles(status: ProjectChange['status']): string {
  if (status === 'blocked') return 'border-warning/40 bg-warning-muted text-warning';
  if (status === 'ready_to_merge' || status === 'merged') return 'border-success/30 bg-success-muted text-success';
  if (status === 'reviewing' || status === 'checking' || status === 'implementing') {
    return 'border-accent/40 bg-accent/10 text-accent-text';
  }
  return 'border bg-surface-alt text-muted';
}

export function readinessStatusStyles(status: ProjectChangeReadinessCheck['status']): string {
  if (status === 'pass') return 'bg-success-muted text-success';
  if (status === 'block') return 'bg-danger-muted text-danger';
  return 'bg-warning-muted text-warning';
}

export function readinessStatusIcon(status: ProjectChangeReadinessCheck['status']): LucideIcon {
  if (status === 'pass') return CheckCircle2;
  if (status === 'block') return XCircle;
  return RefreshCw;
}

export function buildAssistantPrompt(project: Project, summary?: ProjectSummary): string {
  return [
    `Help me move the project "${project.name}" forward.`,
    project.brief?.trim() ? `Current brief: ${project.brief.trim()}` : 'We still need to shape the project brief.',
    `Current signals: ${summary?.conversationCount ?? 0} conversations, ${summary?.artifactCount ?? 0} artifacts, ${summary?.taskCount ?? 0} tasks, ${summary?.workstreamCount ?? 0} workflows.`,
    'Recommend the next project action and create drafts for tasks, workflows, workforce, artifacts, or decisions when useful.',
  ].join('\n\n');
}

export function resourceTypeLabel(type: ProjectThreadResource['resourceType']): string {
  if (type === 'workflow_run') return 'workflow run';
  if (type === 'workforce_member') return 'workforce';
  return type.replace(/_/g, ' ');
}
